import { useState, useRef } from 'react'
import { Upload, FileSpreadsheet, Check, AlertCircle, ChevronDown, ChevronRight, Plus, Table, X } from 'lucide-react'
import * as XLSX from 'xlsx'
import type { ColumnMapping } from '../types'

interface ParsedSheet {
  sheetName: string
  headers: string[]
  rows: Record<string, string>[]
  sourceFile: string
  rawWs: XLSX.WorkSheet
  headerRow: number
}

interface SheetConfig {
  selected: boolean
  mapping: ColumnMapping
  expanded: boolean
  skipRows: number // number of extra header/sub-header rows to skip after the column headers
}

interface ExcelUploaderProps {
  onImport: (rows: Array<{ row_index: number; sheet_name?: string; section?: string; requirement_name?: string; requirement_text: string; requirement_level?: string; original_response?: string }>, filename: string) => void
  disabled?: boolean
}

/** Sanitise headers: drop blanks, XLSX auto-generated __EMPTY keys, deduplicate */
function cleanHeaders(raw: string[]): string[] {
  const seen = new Set<string>()
  const result: string[] = []
  for (const h of raw) {
    const s = String(h ?? '').trim()
    if (!s || seen.has(s) || /^__EMPTY/.test(s)) continue
    seen.add(s)
    result.push(s)
  }
  return result
}

function parseSheetWithHeaderRow(ws: XLSX.WorkSheet, headerRow: number): { headers: string[]; rows: Record<string, string>[] } {
  const jsonData = XLSX.utils.sheet_to_json<Record<string, string>>(ws, { defval: '', range: headerRow - 1 })
  if (jsonData.length === 0) return { headers: [], rows: [] }
  const headers = cleanHeaders(Object.keys(jsonData[0]))
  return { headers, rows: jsonData }
}

function autoDetectHeaderRow(ws: XLSX.WorkSheet): { headerRow: number; headers: string[]; rows: Record<string, string>[] } {
  let bestRow = 1
  let bestHeaders: string[] = []
  let bestRows: Record<string, string>[] = []
  for (let tryRow = 1; tryRow <= 10; tryRow++) {
    const result = parseSheetWithHeaderRow(ws, tryRow)
    if (result.headers.length > bestHeaders.length) {
      bestRow = tryRow
      bestHeaders = result.headers
      bestRows = result.rows
    }
    if (result.headers.length >= 3) break
  }
  return { headerRow: bestRow, headers: bestHeaders, rows: bestRows }
}

function autoDetect(headers: string[]): ColumnMapping {
  return {
    requirement: headers.find(h => /requirement|description|question|detail/i.test(h)) || '',
    level: headers.find(h => /level|priority|mandatory|importance|type/i.test(h)),
    section: headers.find(h => /section|category|group|area|domain/i.test(h)),
    name: headers.find(h => /^name$/i.test(h) || /sub.?section|sub.?heading|topic|item\s*name/i.test(h)),
    response: headers.find(h => /response|answer|reply|comment/i.test(h)),
  }
}

/** Fix common Windows-1252 → UTF-8 mojibake in text */
function fixMojibake(s: string): string {
  return s
    .replace(/\u00e2\u0080\u0093/g, '–')  // en dash
    .replace(/\u00e2\u0080\u0094/g, '—')  // em dash
    .replace(/\u00e2\u0080\u0099/g, '\u2019') // right single quote '
    .replace(/\u00e2\u0080\u0098/g, '\u2018') // left single quote '
    .replace(/\u00e2\u0080\u009c/g, '\u201c') // left double quote "
    .replace(/\u00e2\u0080\u009d/g, '\u201d') // right double quote "
    .replace(/\u00e2\u0080\u00a6/g, '\u2026') // ellipsis …
    .replace(/\u00e2\u0080\u00a2/g, '\u2022') // bullet •
    .replace(/\u00c2\u00a0/g, ' ')             // non-breaking space
    // Catch remaining â + 2-char sequences (common CP1252 mojibake pattern)
    .replace(/â€"/g, '–')
    .replace(/â€"/g, '—')
    .replace(/â€™/g, '\u2019')
    .replace(/â€˜/g, '\u2018')
    .replace(/â€œ/g, '\u201c')
    .replace(/â€[^\w]/g, '\u201d')
    .replace(/â€¦/g, '\u2026')
    .replace(/â€¢/g, '\u2022')
    .replace(/Â /g, ' ')
}

/** Safely read a cell value as a trimmed string */
function cell(row: Record<string, string>, key: string | undefined): string {
  if (!key) return ''
  try {
    return fixMojibake(String(row[key] ?? '').trim())
  } catch {
    return ''
  }
}

/** Get first N non-empty sample values from a column */
function getSampleValues(rows: Record<string, string>[], columnKey: string | undefined, count = 3): string[] {
  if (!columnKey) return []
  const samples: string[] = []
  for (const row of rows) {
    const val = cell(row, columnKey)
    if (val) {
      samples.push(val.length > 100 ? val.substring(0, 100) + '...' : val)
      if (samples.length >= count) break
    }
  }
  return samples
}

export function ExcelUploader({ onImport, disabled }: ExcelUploaderProps) {
  const fileRef = useRef<HTMLInputElement>(null)
  const addFileRef = useRef<HTMLInputElement>(null)
  const [sheets, setSheets] = useState<ParsedSheet[]>([])
  const [configs, setConfigs] = useState<Record<string, SheetConfig>>({})
  const [filenames, setFilenames] = useState<string[]>([])
  const [error, setError] = useState<string | null>(null)

  const parseFile = (file: File, append: boolean) => {
    setError(null)

    const reader = new FileReader()
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target!.result as ArrayBuffer)
        // For CSV files, try codepage 1252 (Windows Latin-1) to avoid mojibake on dashes/quotes
        const isCsv = file.name.toLowerCase().endsWith('.csv')
        const wb = XLSX.read(data, { type: 'array', ...(isCsv ? { codepage: 1252 } : {}) })

        const parsed: ParsedSheet[] = []
        const newConfigs: Record<string, SheetConfig> = {}

        for (const sheetName of wb.SheetNames) {
          try {
            const ws = wb.Sheets[sheetName]
            if (!ws['!ref']) continue

            const { headerRow, headers, rows } = autoDetectHeaderRow(ws)
            if (headers.length === 0) continue

            const uniqueKey = wb.SheetNames.length === 1 ? file.name : `${file.name} › ${sheetName}`
            parsed.push({ sheetName: uniqueKey, headers, rows, sourceFile: file.name, rawWs: ws, headerRow })

            const mapping = autoDetect(headers)
            newConfigs[uniqueKey] = {
              selected: !!mapping.requirement,
              mapping,
              expanded: !!mapping.requirement,
              skipRows: 0,
            }
          } catch (sheetErr) {
            console.warn(`Skipping sheet "${sheetName}":`, sheetErr)
          }
        }

        if (parsed.length === 0) {
          setError('No usable data found in any sheet')
          return
        }

        if (append) {
          setSheets(prev => [...prev, ...parsed])
          setConfigs(prev => ({ ...prev, ...newConfigs }))
          setFilenames(prev => [...prev, file.name])
        } else {
          setSheets(parsed)
          setConfigs(newConfigs)
          setFilenames([file.name])
        }
      } catch {
        setError('Failed to parse Excel file. Make sure it is a valid .xlsx or .xls file.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    parseFile(file, false)
    e.target.value = ''
  }

  const handleAddFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    parseFile(file, true)
    e.target.value = ''
  }

  const changeHeaderRow = (sheetName: string, newRow: number) => {
    setSheets(prev => prev.map(s => {
      if (s.sheetName !== sheetName) return s
      const { headers, rows } = parseSheetWithHeaderRow(s.rawWs, newRow)
      return { ...s, headers, rows, headerRow: newRow }
    }))
    // Re-autodetect mapping with new headers
    const sheet = sheets.find(s => s.sheetName === sheetName)
    if (sheet) {
      const { headers } = parseSheetWithHeaderRow(sheet.rawWs, newRow)
      const mapping = autoDetect(headers)
      setConfigs(prev => ({
        ...prev,
        [sheetName]: { ...prev[sheetName], mapping, skipRows: 0 },
      }))
    }
  }

  const removeSheet = (sheetName: string) => {
    setSheets(prev => prev.filter(s => s.sheetName !== sheetName))
    setConfigs(prev => {
      const next = { ...prev }
      delete next[sheetName]
      return next
    })
  }

  const updateConfig = (sheetName: string, patch: Partial<SheetConfig>) => {
    setConfigs(prev => ({ ...prev, [sheetName]: { ...prev[sheetName], ...patch } }))
  }

  const updateMapping = (sheetName: string, patch: Partial<ColumnMapping>) => {
    setConfigs(prev => ({
      ...prev,
      [sheetName]: {
        ...prev[sheetName],
        mapping: { ...prev[sheetName].mapping, ...patch },
      },
    }))
  }

  const toggleSheet = (sheetName: string) => {
    const current = configs[sheetName]
    updateConfig(sheetName, {
      selected: !current.selected,
      expanded: !current.selected,
    })
  }

  const selectedSheets = sheets.filter(s => configs[s.sheetName]?.selected)
  const totalRows = selectedSheets.reduce((sum, s) => sum + Math.max(0, s.rows.length - (configs[s.sheetName]?.skipRows || 0)), 0)

  const allMapped = selectedSheets.length > 0 && selectedSheets.every(s => configs[s.sheetName]?.mapping.requirement)

  const handleConfirm = () => {
    if (!allMapped) return

    let rowIndex = 0
    const rows: Array<{ row_index: number; sheet_name?: string; section?: string; requirement_name?: string; requirement_text: string; requirement_level?: string; original_response?: string }> = []
    const hasMultipleSheets = selectedSheets.length > 1

    for (const sheet of selectedSheets) {
      const cfg = configs[sheet.sheetName]
      const m = cfg.mapping
      const dataRows = sheet.rows.slice(cfg.skipRows || 0)

      // Extract the clean sheet display name (strip "filename › " prefix)
      const sheetDisplayName = hasMultipleSheets
        ? (sheet.sheetName.includes(' › ') ? sheet.sheetName.split(' › ')[1] : sheet.sheetName)
        : undefined

      for (const row of dataRows) {
        rowIndex++
        const reqText = cell(row, m.requirement)
        if (!reqText) continue

        const sectionVal = cell(row, m.section)
        const nameVal = cell(row, m.name)

        rows.push({
          row_index: rowIndex,
          requirement_text: reqText,
          sheet_name: sheetDisplayName,
          section: sectionVal || undefined,
          requirement_name: nameVal || undefined,
          requirement_level: cell(row, m.level) || undefined,
          original_response: cell(row, m.response) || undefined,
        })
      }
    }

    if (rows.length === 0) {
      setError('No valid requirements found after mapping. Check your column selections.')
      return
    }

    onImport(rows, filenames.join(', '))
  }

  const fileGroups = sheets.reduce<Record<string, ParsedSheet[]>>((acc, s) => {
    const key = s.sourceFile
    if (!acc[key]) acc[key] = []
    acc[key].push(s)
    return acc
  }, {})

  // ── Empty state ──
  if (sheets.length === 0) {
    return (
      <div className="space-y-3">
        <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleFile} className="hidden" />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={disabled}
          className="flex flex-col items-center justify-center w-full py-12 rounded-xl border-2 border-dashed border-[var(--border-color)] hover:border-[var(--accent-color)] bg-[var(--bg-secondary)] hover:bg-[var(--bg-tertiary)] transition-all cursor-pointer disabled:opacity-50"
        >
          <Upload size={28} className="text-[var(--text-muted)] mb-3" />
          <span className="text-sm font-medium text-[var(--text-primary)]">Upload RFP Spreadsheet</span>
          <span className="text-xs text-[var(--text-muted)] mt-1">.xlsx, .xls, or .csv — you can add multiple files</span>
        </button>
        {error && (
          <div className="flex items-center gap-2 text-xs text-red-400">
            <AlertCircle size={14} />
            {error}
          </div>
        )}
      </div>
    )
  }

  // ── Mapping UI ──
  return (
    <div className="space-y-4 rounded-xl border border-[var(--border-color)] bg-[var(--bg-secondary)] p-4">
      <input ref={addFileRef} type="file" accept=".xlsx,.xls,.csv" onChange={handleAddFile} className="hidden" />

      {/* Summary header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <FileSpreadsheet size={18} className="text-green-400" />
          <div>
            <p className="text-sm font-medium text-[var(--text-primary)]">
              {filenames.length} file{filenames.length !== 1 ? 's' : ''} — {sheets.length} sheet{sheets.length !== 1 ? 's' : ''}
            </p>
            <p className="text-xs text-[var(--text-muted)]">
              {selectedSheets.length} selected, {totalRows} rows total
            </p>
          </div>
        </div>
        <button
          onClick={() => addFileRef.current?.click()}
          className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-lg border border-dashed border-[var(--border-color)] text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] hover:border-[var(--text-muted)] transition-colors"
        >
          <Plus size={13} />
          Add another file
        </button>
      </div>

      {/* Multi-sheet info */}
      {sheets.length > 1 && (
        <p className="text-xs text-[var(--text-secondary)]">
          Sheet names are automatically captured as context for AI responses. You can also map Section and Name columns within each sheet for finer categorization.
        </p>
      )}

      {/* Per-file → per-sheet mapping */}
      <div className="space-y-4">
        {Object.entries(fileGroups).map(([sourceFile, fileSheets]) => (
          <div key={sourceFile} className="space-y-2">
            {/* File header bar */}
            <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-[var(--bg-primary)] border border-[var(--border-color)]">
              <FileSpreadsheet size={14} className="text-green-400 shrink-0" />
              <span className="text-xs font-semibold text-[var(--text-primary)] flex-1 truncate">{sourceFile}</span>
              <span className="text-[10px] text-[var(--text-muted)]">{fileSheets.length} sheet{fileSheets.length !== 1 ? 's' : ''}</span>
            </div>

            {/* Sheet cards */}
            <div className="space-y-2 pl-3 border-l-2 border-[var(--border-color)] ml-2">
              {fileSheets.map(sheet => {
                const cfg = configs[sheet.sheetName]
                if (!cfg) return null
                const m = cfg.mapping
                const isValid = !!m.requirement
                const displayName = sheet.sheetName.includes(' › ')
                  ? sheet.sheetName.split(' › ')[1]
                  : sheet.sheetName
                const dataRows = sheet.rows.slice(cfg.skipRows || 0)

                return (
                  <div
                    key={sheet.sheetName}
                    className={`rounded-lg border overflow-hidden transition-all ${
                      cfg.selected
                        ? 'border-[var(--border-color)] bg-[var(--bg-primary)]'
                        : 'border-[var(--border-color)] bg-[var(--bg-primary)] opacity-40'
                    }`}
                  >
                    {/* Sheet header */}
                    <div
                      className="flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-[var(--bg-tertiary)] transition-colors"
                      onClick={() => {
                        if (cfg.selected) updateConfig(sheet.sheetName, { expanded: !cfg.expanded })
                      }}
                    >
                      <button
                        onClick={e => { e.stopPropagation(); toggleSheet(sheet.sheetName) }}
                        className={`w-4 h-4 rounded-sm border flex items-center justify-center shrink-0 transition-colors ${
                          cfg.selected ? 'bg-indigo-600 border-indigo-600' : 'border-[var(--border-color)] hover:border-[var(--text-muted)]'
                        }`}
                      >
                        {cfg.selected && <Check size={10} className="text-white" />}
                      </button>

                      {cfg.selected && (
                        cfg.expanded
                          ? <ChevronDown size={14} className="text-[var(--text-muted)] shrink-0" />
                          : <ChevronRight size={14} className="text-[var(--text-muted)] shrink-0" />
                      )}

                      <Table size={13} className={cfg.selected ? 'text-indigo-400 shrink-0' : 'text-[var(--text-muted)] shrink-0'} />
                      <span className={`text-sm font-medium flex-1 ${cfg.selected ? 'text-[var(--text-primary)]' : 'text-[var(--text-muted)] line-through'}`}>
                        {displayName}
                      </span>

                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] text-[var(--text-muted)]">
                          {sheet.rows.length} rows · {sheet.headers.length} cols · row {sheet.headerRow} headers
                        </span>
                        {!cfg.selected && (
                          <span className="text-[10px] text-[var(--text-muted)] font-medium px-1.5 py-0.5 rounded bg-[var(--bg-tertiary)]">Skipped</span>
                        )}
                        {cfg.selected && !isValid && (
                          <span className="text-[10px] text-amber-400 font-medium px-1.5 py-0.5 rounded bg-amber-500/10">Needs mapping</span>
                        )}
                        {cfg.selected && isValid && (
                          <span className="text-[10px] text-green-400 font-medium px-1.5 py-0.5 rounded bg-green-500/10">Ready</span>
                        )}
                        <button
                          onClick={e => { e.stopPropagation(); removeSheet(sheet.sheetName) }}
                          className="p-0.5 text-[var(--text-muted)] hover:text-red-400 transition-colors"
                          title="Remove sheet"
                        >
                          <X size={12} />
                        </button>
                      </div>
                    </div>

                    {/* Expanded mapping */}
                    {cfg.expanded && cfg.selected && (
                      <div className="border-t border-[var(--border-color)] px-3 py-3 space-y-3">
                        {/* Header row selector */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs text-[var(--text-muted)] shrink-0">Header row</label>
                          <input
                            type="number"
                            min={1}
                            max={20}
                            value={sheet.headerRow}
                            onChange={e => {
                              const val = parseInt(e.target.value)
                              if (val >= 1 && val <= 20) changeHeaderRow(sheet.sheetName, val)
                            }}
                            className="w-16 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1.5 text-sm text-[var(--text-primary)] text-center focus:outline-none focus:border-indigo-500"
                          />
                          <span className="text-[10px] text-[var(--text-muted)]">
                            {sheet.headers.length} columns found: {sheet.headers.slice(0, 5).join(', ')}{sheet.headers.length > 5 ? `, +${sheet.headers.length - 5} more` : ''}
                          </span>
                        </div>

                        {/* Column mapping with inline previews */}
                        <div className="space-y-2">
                          <ColumnSelect
                            label="Requirement / Question"
                            required
                            value={m.requirement}
                            headers={sheet.headers}
                            rows={dataRows}
                            onChange={val => updateMapping(sheet.sheetName, { requirement: val })}
                          />
                          <ColumnSelect
                            label="Section / Category"
                            value={m.section || ''}
                            headers={sheet.headers}
                            rows={dataRows}
                            onChange={val => updateMapping(sheet.sheetName, { section: val || undefined })}
                          />
                          <ColumnSelect
                            label="Name / Sub-heading"
                            value={m.name || ''}
                            headers={sheet.headers}
                            rows={dataRows}
                            onChange={val => updateMapping(sheet.sheetName, { name: val || undefined })}
                          />
                          <ColumnSelect
                            label="Requirement Level"
                            value={m.level || ''}
                            headers={sheet.headers}
                            rows={dataRows}
                            onChange={val => updateMapping(sheet.sheetName, { level: val || undefined })}
                          />
                          <ColumnSelect
                            label="Existing Response"
                            value={m.response || ''}
                            headers={sheet.headers}
                            rows={dataRows}
                            onChange={val => updateMapping(sheet.sheetName, { response: val || undefined })}
                          />
                        </div>

                        {/* Rows to skip */}
                        <div className="flex items-center gap-3">
                          <label className="text-xs text-[var(--text-muted)] shrink-0">Skip rows after header</label>
                          <input
                            type="number"
                            min={0}
                            max={sheet.rows.length}
                            value={cfg.skipRows}
                            onChange={e => updateConfig(sheet.sheetName, { skipRows: Math.max(0, parseInt(e.target.value) || 0) })}
                            className="w-16 rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] px-2 py-1.5 text-sm text-[var(--text-primary)] text-center focus:outline-none focus:border-indigo-500"
                          />
                          <span className="text-[10px] text-[var(--text-muted)]">e.g. sub-header or instruction rows</span>
                        </div>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          </div>
        ))}
      </div>

      {error && (
        <div className="flex items-center gap-2 text-xs text-red-400">
          <AlertCircle size={14} />
          {error}
        </div>
      )}

      <div className="flex justify-end gap-2">
        <button
          onClick={() => { setSheets([]); setConfigs({}); setFilenames([]); setError(null) }}
          className="px-3 py-2 text-sm rounded-lg text-[var(--text-secondary)] hover:bg-[var(--bg-tertiary)] transition-colors"
        >
          Cancel
        </button>
        <button
          onClick={handleConfirm}
          disabled={!allMapped || disabled}
          className="flex items-center gap-2 px-4 py-2 text-sm rounded-lg bg-indigo-600 text-white hover:bg-indigo-500 transition-colors disabled:opacity-50"
        >
          <Check size={14} />
          Import from {selectedSheets.length} sheet{selectedSheets.length !== 1 ? 's' : ''} ({totalRows} rows)
        </button>
      </div>
    </div>
  )
}

/** A column selector with inline sample value preview */
function ColumnSelect({ label, required, value, headers, rows, onChange }: {
  label: string
  required?: boolean
  value: string
  headers: string[]
  rows: Record<string, string>[]
  onChange: (val: string) => void
}) {
  const samples = getSampleValues(rows, value || undefined, 3)

  return (
    <div className="rounded-lg border border-[var(--border-color)] bg-[var(--bg-secondary)] p-2.5">
      <div className="flex items-center gap-3">
        <label className="text-xs text-[var(--text-muted)] shrink-0 w-36">
          {label} {required && <span className="text-red-400">*</span>}
        </label>
        <select
          value={value}
          onChange={e => onChange(e.target.value)}
          className="flex-1 rounded-lg border border-[var(--border-color)] bg-[var(--bg-primary)] px-2.5 py-1.5 text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
        >
          <option value="">{required ? 'Select column...' : 'None'}</option>
          {headers.map(h => <option key={h} value={h}>{h}</option>)}
        </select>
      </div>
      {value && samples.length > 0 && (
        <div className="mt-2 ml-[9.5rem] space-y-1">
          {samples.map((s, i) => (
            <p key={i} className="text-[11px] text-[var(--text-secondary)] leading-snug pl-2 border-l-2 border-indigo-500/30">
              {s}
            </p>
          ))}
        </div>
      )}
    </div>
  )
}
